















import {
  SfPrice,
  SfQuantitySelector,
  SfRadio,
  SfButton,
  SfAlert,
} from '@storefront-ui/vue';
import {
  computed, defineComponent, PropType, ref, watch,
} from '@nuxtjs/composition-api';
import {
  getBundleProducts,
  getName as getProductName
} from '@wemade-vsf/catalog/lib/runtime/product/getters/productGetters';
import { decodeUid } from '@wemade-vsf/core';
import { useCustomerPricing } from '@wemade-vsf/catalog/lib/runtime/product/composables/useCustomerPricing';
import { bundleProductInitialSelector, SelectedBundleProductOptions } from '@wemade-vsf/catalog/lib/runtime/product/helpers/bundleProduct';
import type { WithTypename } from '@wemade-vsf/catalog/lib/runtime/product/types';
import type { BundleProduct, BundleItem } from '@wemade-vsf/magento-api';

import { useCart } from '@wemade-vsf/customer';

export default defineComponent({
  name: 'BundleProductSelector',
  components: {
    SfButton,
    SfPrice,
    SfQuantitySelector,
    SfRadio,
    SfAlert,
    checkboxType: () => import('components/catalog/Product/ProductTypes/bundle/Renderer/CheckboxRenderer.vue'),
    radioType: () => import('components/catalog/Product/ProductTypes/bundle/Renderer/RadioRenderer.vue'),
    multiType: () => import('components/catalog/Product/ProductTypes/bundle/Renderer/MultiRenderer.vue'),
    dropdownType: () => import('components/catalog/Product/ProductTypes/bundle/Renderer/DropdownRenderer.vue'),
  },
  props: {
    canAddToCart: {
      type: Boolean,
      required: false,
      default: true,
    },
    showAddToCart: {
      type: Boolean,
      required: false,
      default: true
    },
    product: {
      type: Object as PropType<WithTypename<BundleProduct>>,
      required: true,
    },
  },
  emits: ['update-bundle', 'update-price'],
  setup(props, { emit }) {
    const { loading, addItem, error: cartError } = useCart();
    const bundleProduct = computed(() => getBundleProducts(props.product));
    const selectedOptions = ref<SelectedBundleProductOptions>({});
    const addToCartError = computed(() => cartError.value?.addItem?.message);
    const { getPriceForProduct } = useCustomerPricing();

    if (bundleProduct.value) {
      selectedOptions.value = bundleProductInitialSelector(bundleProduct.value);
    }
    
    const rendererTypes = {
      'checkbox': 'checkboxType',
      'radio': 'radioType',
      'multi': 'multiType',
      'select': 'dropdownType',
    }

    const price = computed(() => Object.keys(selectedOptions.value)
      .reduce(
        (s, k) => {
          const option = selectedOptions.value[k];
          if (Array.isArray(option)) {
            option.forEach((o) => {
              s = s + Number.parseFloat(String(o.price)) * o.quantity;
            })
          } else {
            s = s + Number.parseFloat(String(option.price)) * option.quantity;
          }
          return s;
        },
        0
      ));

   
    function getBundleProductData() {
      return  Object.keys(selectedOptions.value).map((selectedOptionKey) => {
        const selectedOption = selectedOptions.value[selectedOptionKey];
        const decoded = decodeUid(selectedOptionKey)
        const id = decoded ? parseInt(decoded.replace('bundle/', '')) : null;

        const decodeOption = (optionUid: string) => {
          const decoded = decodeUid(optionUid);
          const parts = decoded!.split('/');
          return parts[2] ? String(parts[2]) : null;
        }

        if (!id) {
          return null
        }
        return Array.isArray(selectedOption)
        ? {
          id,
          quantity: 1,
          value: selectedOption.map((option) => decodeOption(option.uid))
        }
        : {
          id,
          quantity: selectedOption.quantity,
          value: decodeOption(selectedOption.uid)
        };
      });
    }

    function updateSelectedOptions(newOptions, bundle: BundleItem) {
      if (newOptions === null) {
        selectedOptions.value = Object.keys(selectedOptions.value).reduce((acc, key) => {
          if (key !== bundle.uid) {
            acc[key] = selectedOptions.value[key];
          }
          return acc;
        }, {});
        return;
      }
      selectedOptions.value = {
        ...selectedOptions.value,
        [bundle.uid!]: newOptions,
      };
    }

    watch(
      bundleProduct,
      (newVal) => {
        selectedOptions.value = bundleProductInitialSelector(newVal);
      },
      { deep: true },
    );

    watch(
      computed(() => selectedOptions.value),
      () => {
        emit('update-bundle', getBundleProductData());
        emit('update-price', price.value);
      },
      { deep: true },
    );

    return {
      bundleProduct,
      updateSelectedOptions,
      loading,
      price,
      getProductName,
      getProductPrice: getPriceForProduct,
      selectedOptions,
      addToCartError,
      rendererTypes
    };
  },
});
